import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Copy, Key, Link2, MoreHorizontal, Play, Plus, Search, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination } from '@/components/pagination';
import { CopyId, PageHeader, Segmented, StatStrip, StatusPill } from '@/components/page';
import { EmptyState, GRANT_SHORT, SkeletonRows, Tag, fmtDate } from '@/components/oauth';
import { useAuth, useClients, useDebouncedValue, useProducts, useToast } from '@/hooks';
import type { ClientType } from '@/types';

const PAGE_SIZE = 10;
const ALL_APPS = '__all__';

type TypeFilter = 'all' | ClientType | 'disabled';

export function ClientsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const canManage = user?.is_super_admin === true;

  const [search, setSearch] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [page, setPage] = useState(1);
  const app = params.get('product') || '';
  const debounced = useDebouncedValue(search).trim().toLowerCase();

  const { data: clients = [], isLoading, isError, refetch } = useClients();
  const { data: products = [] } = useProducts();

  const counts = useMemo(
    () => ({
      all: clients.length,
      public: clients.filter((c) => c.client_type === 'public').length,
      confidential: clients.filter((c) => c.client_type === 'confidential').length,
      disabled: clients.filter((c) => !c.is_active).length,
    }),
    [clients]
  );

  const filtered = useMemo(
    () =>
      clients
        .filter((c) =>
          type === 'all' ? true : type === 'disabled' ? !c.is_active : c.client_type === type
        )
        .filter((c) => !app || c.product_id === app)
        .filter(
          (c) =>
            !debounced ||
            [c.name, c.client_id, c.product_name ?? '', ...c.redirect_uris].some((v) =>
              v.toLowerCase().includes(debounced)
            )
        )
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [clients, type, app, debounced]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const newHref = `/clients/new${app ? `?product=${encodeURIComponent(app)}` : ''}`;

  const setApp = (v: string) => {
    const next = new URLSearchParams(params);
    if (v && v !== ALL_APPS) next.set('product', v);
    else next.delete('product');
    setParams(next, { replace: true });
    setPage(1);
  };
  const clearFilters = () => {
    setSearch('');
    setType('all');
    setApp('');
  };

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast({ title: 'Copied to clipboard', description: 'client_id' });
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div>
      <PageHeader
        title="OAuth clients"
        description={
          canManage
            ? 'Credentials your apps use to sign users in. Public clients rely on PKCE; confidential clients authenticate with a secret.'
            : 'OAuth clients for the apps you can access.'
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/playground">
                <Play className="mr-2 h-4 w-4" />
                Test a flow
              </Link>
            </Button>
            {canManage && (
              <Button asChild>
                <Link to={newHref}>
                  <Plus className="mr-2 h-4 w-4" />
                  New client
                </Link>
              </Button>
            )}
          </>
        }
      />

      <div className="mb-5">
        <StatStrip
          items={[
            { label: 'Clients', value: counts.all, note: `across ${products.length} apps` },
            { label: 'Public', value: counts.public, note: 'PKCE, no secret' },
            { label: 'Confidential', value: counts.confidential, note: 'secret-authenticated' },
            { label: 'Disabled', value: counts.disabled, note: 'cannot issue tokens' },
          ]}
        />
      </div>

      <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label="Search clients"
              placeholder="Search name, client ID or redirect URI"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
              autoComplete="off"
            />
          </div>
          <Segmented
            value={type}
            onChange={(v) => {
              setType(v);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All', count: counts.all },
              { value: 'public', label: 'Public', count: counts.public },
              { value: 'confidential', label: 'Confidential', count: counts.confidential },
            ]}
          />
          <Select value={app || ALL_APPS} onValueChange={setApp}>
            <SelectTrigger aria-label="Filter by app" className="w-full min-w-[180px] max-w-[260px] sm:w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_APPS}>All apps</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <SkeletonRows rows={5} />
        ) : isError ? (
          <EmptyState
            icon={<Key />}
            title="Couldn’t load clients"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          >
            Check your connection and try again.
          </EmptyState>
        ) : counts.all === 0 ? (
          <EmptyState
            icon={<Key />}
            title="No OAuth clients yet"
            action={
              canManage ? (
                <Button asChild>
                  <Link to={newHref}>
                    <Plus className="mr-2 h-4 w-4" />
                    New client
                  </Link>
                </Button>
              ) : undefined
            }
          >
            A client is how an application signs users in through authrz.me. Create one per app
            surface — web, mobile, CLI or server.
          </EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Search />}
            title="No clients match"
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          >
            Try a different name, client ID or redirect URI, or clear the filters.
          </EmptyState>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Grant types</TableHead>
                  <TableHead className="text-right">Redirects</TableHead>
                  <TableHead>PKCE</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => {
                  const href = `/clients/${c.id}`;
                  return (
                    <TableRow
                      key={c.id}
                      className={`${canManage ? 'cursor-pointer' : ''} ${c.is_active ? '' : 'opacity-60'}`}
                      onClick={(e) => {
                        if (canManage && !(e.target as HTMLElement).closest('a,button,input'))
                          navigate(href);
                      }}
                    >
                      <TableCell className="min-w-[200px] max-w-[320px]">
                        <div className="grid gap-0.5">
                          {canManage ? (
                            <Link
                              to={href}
                              title={c.name}
                              className="truncate font-semibold hover:underline hover:underline-offset-4"
                            >
                              {c.name}
                            </Link>
                          ) : (
                            <span className="truncate font-semibold">{c.name}</span>
                          )}
                          <CopyId value={c.client_id} className="max-w-[260px] border-0 bg-transparent px-0 text-muted-foreground" />
                        </div>
                      </TableCell>
                      <TableCell>
                        {canManage ? (
                          <Link
                            to={`/products/${c.product_id}`}
                            className="whitespace-nowrap hover:underline hover:underline-offset-4"
                          >
                            {c.product_name || 'Unknown app'}
                          </Link>
                        ) : (
                          <span className="whitespace-nowrap">{c.product_name || 'Unknown app'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tag>{c.client_type}</Tag>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {c.allowed_grant_types.map((g) => (
                            <Tag key={g} solid>
                              {GRANT_SHORT[g] || g}
                            </Tag>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {c.redirect_uris.length}
                      </TableCell>
                      <TableCell>
                        {c.pkce_required ? (
                          <StatusPill tone="ok">Required</StatusPill>
                        ) : (
                          <StatusPill>Optional</StatusPill>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusPill tone={c.is_active ? 'ok' : 'neutral'}>
                          {c.is_active ? 'Active' : 'Disabled'}
                        </StatusPill>
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-[12.5px] tabular-nums">
                        {fmtDate(c.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={`Actions for ${c.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canManage && (
                              <>
                                <DropdownMenuItem onClick={() => navigate(href)}>
                                  <ArrowRight className="mr-2 h-4 w-4" />
                                  Open client
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`${href}#redirects`)}>
                                  <Link2 className="mr-2 h-4 w-4" />
                                  Edit redirect URIs
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => void copyId(c.client_id)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Copy client ID
                            </DropdownMenuItem>
                            {c.client_type === 'public' && c.is_active && (
                              <DropdownMenuItem onClick={() => navigate(`/playground?client=${c.id}`)}>
                                <Play className="mr-2 h-4 w-4" />
                                Test in playground
                              </DropdownMenuItem>
                            )}
                            {canManage && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate(`${href}#settings`)}>
                                  <Settings className="mr-2 h-4 w-4" />
                                  Settings
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination
              pagination={{
                page: current,
                pageSize: PAGE_SIZE,
                total: filtered.length,
                totalPages,
              }}
              onPageChange={setPage}
              itemLabel="clients"
              className="p-3"
            />
          </>
        )}
      </div>
    </div>
  );
}
